// Bridges the OIDC auth state (managed by react-oidc-context inside React) to the
// axios layer (created outside React). AuthBridge pushes the current access token
// here; api-utils reads it per request. The selected instance is the user's chosen
// tenant, sent as the X-Instance-Id header and validated server-side.
import { useSyncExternalStore } from "react";

let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
    accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

const INSTANCE_KEY = "selectedInstanceId";
const instanceListeners = new Set<() => void>();

export const setSelectedInstanceId = (id: number | null) => {
    if (id == null) {
        localStorage.removeItem(INSTANCE_KEY);
    } else {
        localStorage.setItem(INSTANCE_KEY, String(id));
    }
    instanceListeners.forEach(listener => listener());
};

export const getSelectedInstanceId = (): number | null => {
    const value = localStorage.getItem(INSTANCE_KEY);
    return value ? Number(value) : null;
};

/**
 * The chosen company as React state, so tenant-scoped queries can wait for it and refetch when
 * it changes. (Fetching before a company is chosen returned nothing and raced the refetch.)
 */
export const useSelectedInstanceId = (): number | null => useSyncExternalStore(
    (listener) => {
        instanceListeners.add(listener);
        return () => { instanceListeners.delete(listener); };
    },
    getSelectedInstanceId,
);

// ---- where to go after signing in ------------------------------------------------------------

const RETURN_TO_KEY = "oms.returnTo";
/** Paths that are part of signing in, never a destination. */
const NOT_A_DESTINATION = ["/", "/signin", "/select-instance"];

/** Remember the page the user asked for before the sign-in redirect / company choice. */
export const rememberReturnTo = (path: string) => {
    if (NOT_A_DESTINATION.includes(path.split("?")[0])) {
        return;
    }
    try { sessionStorage.setItem(RETURN_TO_KEY, path); } catch { /* storage off: just land on the start page */ }
};

/** The remembered page, once (it is forgotten as it is read). */
export const consumeReturnTo = (): string | null => {
    try {
        const path = sessionStorage.getItem(RETURN_TO_KEY);
        sessionStorage.removeItem(RETURN_TO_KEY);
        return path;
    } catch {
        return null;
    }
};
